import { useCallback, useEffect, useState } from "react";
import { CheckCircle2, ShieldCheck, CalendarClock } from "lucide-react";
import { api, ApiError } from "@main/api/client";
import { LoadingBlock } from "@main/components/Spinner";
import { useFrameworks } from "@main/context/FrameworksContext";
import { useCurrentUser } from "@main/auth/useCurrentUser";
import { theme, card, btnSecondary, pageContainer, pageHeading, pageSubtitle } from "@main/theme";

// ── My Package (Jersey edition) ───────────────────────────────────────────
// What the Jersey package includes, where its three-year term stands, how it is
// billed (monthly or annually — the client's choice), this month's platform fee
// and the certificate choice (Cyber Essentials, or Cyber Essentials Plus on top).
// Figures come from GET /usage, which is platformFeeBreakdown() and
// jerseyPackageSummary() in backend/src/lib/pricing.ts — never computed here.

type Interval = "monthly" | "annual";

interface JerseyPackage {
  annualPence: number;
  monthlyPence: number;
  certificateAnnualPence: number;
  cepAnnualPence: number;
  cepMonthlyPence: number;
  termMonths: number;
  consultancyHoursByYear: number[];
  consultancyHoursThisYear: number;
  aiCreditPence: number;
  billingInterval: Interval;
  nextAnnualChargeAt: string | null;
  includes: string[];
  termStartsAt: string | null;
  termEndsAt: string | null;
  termYear: number | null;
}

interface Usage {
  billingModel: string;
  jerseyPackage: JerseyPackage | null;
  bundleFlatPence: number;
  standardsFeePence: number;
  integrationAddonPence: number;
  aiCreditPence: number;
  platformFeePence: number;
  currentMonthSpendPence: number;
}

const gbp = (pence: number) => "£" + (pence / 100).toLocaleString("en-GB", { minimumFractionDigits: pence % 100 ? 2 : 0, maximumFractionDigits: 2 });
const date = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" }) : "—");

function Row({ label, value, strong = false }: { label: string; value: string; strong?: boolean }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", gap: 12, padding: "8px 0", borderBottom: `1px solid ${theme.color.border}`, fontSize: 13 }}>
      <span style={{ color: theme.color.muted }}>{label}</span>
      <span style={{ color: theme.color.heading, fontWeight: strong ? 700 : 500, textAlign: "right" }}>{value}</span>
    </div>
  );
}

function Choice({ selected, onClick, disabled, locked = false, title, detail }: { selected: boolean; onClick: () => void; disabled: boolean; locked?: boolean; title: string; detail: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-pressed={selected}
      style={{
        flex: "1 1 200px", textAlign: "left", padding: 12, borderRadius: theme.radius.md, cursor: locked ? "default" : disabled ? "wait" : "pointer",
        border: `1.5px solid ${selected ? theme.color.accent : theme.color.border}`,
        background: selected ? theme.color.accentBg : "transparent", fontFamily: "inherit",
      }}
    >
      <span style={{ display: "block", fontSize: 13, fontWeight: 600, color: theme.color.heading }}>{title}</span>
      <span style={{ display: "block", fontSize: 12, color: theme.color.muted, marginTop: 3 }}>{detail}</span>
    </button>
  );
}

export function JsyPackagePage() {
  const { enrolled, reload } = useFrameworks();
  const { user } = useCurrentUser();
  // Both changes are org:admin on the server (PATCH /organizations/jersey-billing and
  // PUT /organizations/frameworks); everyone else sees the choice without the controls.
  const canChange = !!user?.permissions?.includes("org:admin");
  const [usage, setUsage] = useState<Usage | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get<Usage>("/usage").then(setUsage).catch(() => setError("Could not load your package."));
  }, []);
  useEffect(load, [load]);

  const plus = enrolled.has("cyber_essentials_plus");

  const run = async (action: () => Promise<unknown>, failure: string) => {
    setBusy(true);
    setError(null);
    try {
      await action();
      load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : failure);
    } finally {
      setBusy(false);
    }
  };
  const switchCertificate = (target: "cyber_essentials" | "cyber_essentials_plus") =>
    run(async () => { await api.put("/organizations/frameworks", { frameworkIds: [target] }); reload(); }, "Could not change your certification.");
  const switchInterval = (interval: Interval) =>
    run(() => api.patch("/organizations/jersey-billing", { interval }), "Could not change how your package is billed.");

  if (!usage && !error) return <div style={pageContainer}><LoadingBlock /></div>;
  const pkg = usage?.jerseyPackage ?? null;
  const licensed = usage?.billingModel === "jersey_licensed";

  return (
    <div style={pageContainer}>
      <h1 style={pageHeading}>My Package</h1>
      <p style={pageSubtitle}>
        {licensed
          ? "Your consultancy provides Abacas Jersey and bills you directly."
          : pkg
            ? `Jersey Cyber Essentials Package — ${gbp(pkg.annualPence)} a year over a ${pkg.termMonths / 12}-year term.`
            : "Jersey Cyber Essentials Package."}
      </p>

      {error && <div role="alert" style={{ ...card, color: theme.color.dangerText, marginBottom: 16 }}>{error}</div>}

      {pkg && !licensed && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 16, marginBottom: 16 }}>
          <div style={card}>
            <div style={{ fontSize: 13, fontWeight: 700, color: theme.color.heading, marginBottom: 10 }}>What your package includes</div>
            <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "grid", gap: 8 }}>
              {pkg.includes.map((item) => (
                <li key={item} style={{ display: "flex", gap: 8, alignItems: "flex-start", fontSize: 13, color: theme.color.text }}>
                  <CheckCircle2 size={15} color="var(--c-success)" style={{ flexShrink: 0, marginTop: 2 }} />
                  {item}
                </li>
              ))}
            </ul>
          </div>
          <div style={card}>
            <div style={{ fontSize: 13, fontWeight: 700, color: theme.color.heading, marginBottom: 6 }}>Your term</div>
            <Row label="Package" value={`${gbp(pkg.annualPence)} a year`} />
            {pkg.termStartsAt ? (
              <>
                <Row label="Started" value={date(pkg.termStartsAt)} />
                <Row label="Ends" value={date(pkg.termEndsAt)} />
                <Row label="Current year" value={pkg.termYear ? `Year ${pkg.termYear} of 3` : "—"} strong />
              </>
            ) : (
              // No start recorded: say so rather than three dashes beside a three-year price (AUD-28).
              <div style={{ fontSize: 12, color: theme.color.subtle, margin: "6px 0" }}>
                The start of your three-year term has not been recorded yet. Your consultant records it when the package begins; until then the hours below are those of year one.
              </div>
            )}
            <Row label="Consultancy hours this year" value={`${pkg.consultancyHoursThisYear} hours`} />
          </div>
        </div>
      )}

      {pkg && !licensed && (
        <div style={{ ...card, marginBottom: 16 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: theme.color.heading, marginBottom: 10, display: "flex", alignItems: "center", gap: 8 }}>
            <CalendarClock size={16} color="var(--c-accent)" /> How you pay
          </div>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <Choice
              selected={pkg.billingInterval === "monthly"}
              disabled={busy || !canChange}
              locked={!canChange}
              onClick={() => pkg.billingInterval !== "monthly" && switchInterval("monthly")}
              title="Monthly"
              detail={`${gbp(pkg.monthlyPence)} a month${plus ? `, plus ${gbp(pkg.cepMonthlyPence)} for Cyber Essentials Plus` : ""}`}
            />
            <Choice
              selected={pkg.billingInterval === "annual"}
              disabled={busy || !canChange}
              locked={!canChange}
              onClick={() => pkg.billingInterval !== "annual" && switchInterval("annual")}
              title="Annually in advance"
              detail={`${gbp(pkg.annualPence)} a year${plus ? `, plus ${gbp(pkg.cepAnnualPence)} for Cyber Essentials Plus` : ""}`}
            />
          </div>
          {!canChange && (
            <p style={{ fontSize: 12, color: theme.color.muted, margin: "10px 0 0" }}>
              Only an organisation administrator can change how the package is billed.
            </p>
          )}
          {pkg.billingInterval === "annual" && pkg.nextAnnualChargeAt && (
            <p style={{ fontSize: 12, color: theme.color.muted, margin: "10px 0 0" }}>
              Your next annual charge is on {date(pkg.nextAnnualChargeAt)}.
            </p>
          )}
        </div>
      )}

      <div style={{ ...card, marginBottom: 16 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: theme.color.heading, marginBottom: 10, display: "flex", alignItems: "center", gap: 8 }}>
          <ShieldCheck size={16} color="var(--c-accent)" /> Certification
        </div>
        <p style={{ fontSize: 13, color: theme.color.muted, margin: "0 0 12px", lineHeight: 1.6 }}>
          You are working towards <strong style={{ color: theme.color.heading }}>{plus ? "Cyber Essentials Plus" : "Cyber Essentials"}</strong>.
          {pkg && !licensed && (plus
            ? ` Cyber Essentials Plus adds an assessor's hands-on test of your devices, at ${gbp(pkg.cepAnnualPence)} a year on top of your package.`
            : ` Your Cyber Essentials certificate and its surveillance are included. Cyber Essentials Plus adds an assessor's hands-on test of your devices, at ${gbp(pkg.cepAnnualPence)} a year on top.`)}
        </p>
        {canChange ? (
          <button
            type="button"
            style={{ ...btnSecondary, opacity: busy ? 0.6 : 1 }}
            disabled={busy}
            onClick={() => switchCertificate(plus ? "cyber_essentials" : "cyber_essentials_plus")}
          >
            {busy ? "Saving…" : plus ? "Switch to Cyber Essentials" : "Move up to Cyber Essentials Plus"}
          </button>
        ) : (
          <p style={{ fontSize: 12, color: theme.color.muted, margin: 0 }}>
            Only an organisation administrator can change the certificate you are working towards.
          </p>
        )}
      </div>

      {usage && (
        <div style={card}>
          <div style={{ fontSize: 13, fontWeight: 700, color: theme.color.heading, marginBottom: 6 }}>This month</div>
          {!licensed && <Row label="Package" value={usage.bundleFlatPence ? gbp(usage.bundleFlatPence) : "Paid annually"} />}
          {usage.standardsFeePence > 0 && <Row label="Cyber Essentials Plus" value={gbp(usage.standardsFeePence)} />}
          {usage.integrationAddonPence > 0 && <Row label="Security connectors" value={gbp(usage.integrationAddonPence)} />}
          {usage.aiCreditPence > 0 && <Row label="AI credit included" value={gbp(usage.aiCreditPence)} />}
          <Row label="AI usage so far" value={gbp(usage.currentMonthSpendPence)} />
          <Row label="Platform fee" value={gbp(usage.platformFeePence)} strong />
        </div>
      )}
    </div>
  );
}
